import * as React from 'react';
import styled from 'styled-components';

import Button from 'src/SharedComponents/Button';
import Dialog from 'src/SharedComponents/Dialog/Dialog';
import DialogActions from 'src/SharedComponents/Dialog/DialogActions';
import DialogContent from 'src/SharedComponents/Dialog/DialogContent';
import DialogTitle from 'src/SharedComponents/Dialog/DialogTitle';
import EmptyStateSplashContainer from 'src/SharedComponents/EmptyStateSplash';
import Label from 'src/SharedComponents/Label';
import ListItem from 'src/SharedComponents/List/ListItem';
import LoadingIndicator from 'src/SharedComponents/LoadingIndicator';
import ViewContainer from 'src/SharedComponents/ViewContainer';
import ManagerTitleBar from 'src/Views/Manager/ManagerTitleBar';

import AgentDetails from "./AgentDetails";
import CreateAgentDialog from "./CreateAgentDialog";

import transparentRoleIcon from 'src/images/customIcons/transparentRole.png';
import { faBars } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { SessionContext } from 'src/Views/SessionContext';

import useManagerAPI from 'src/CustomHooks/useManagerAPI';

import IAgent from 'src/ServerEntities/IAgent';

interface IProps {
    tablabel: string
}

interface IDisplayMenu {
    displayMenu: boolean
}

const AgentListContainer = styled.div<IDisplayMenu>`
    border-right: 1px solid #e5e5e5;
    background-color: white;
    max-width: 320px;
    min-width: 320px;
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    z-index: 2;

    @keyframes slideIn {
        from {
          transform: translateX(-100%);
        }
        to {
          transform: translateX(0);
        }
    }

    @keyframes slideOut {
        from {
          transform: translateX(0);
        }
        to {
          transform: translateX(-100%);
        }
    }

    @media (min-width: 350px) and (max-width: 768px) {
        position: absolute;
        height: 100%;
        display: ${props => (props.displayMenu ? "flex" : "none")};
        animation: ${props => (props.displayMenu ? "slideIn" : "slideOut")} 0.5s ease-in;
    }
`;

const AgentHeader = styled.h4`
    display: flex;
    align-items: center;
    justify-content: space-between;
    text-transform: uppercase;
    font-size: 12px;
    margin: 0;
    padding: 12px 0px 6px 12px;
    z-index: 2;
    height: 50%;

    @media (min-width: 768px) {
        img {
            display: none;
        }
    }
`;

const AgentList = styled.div`
    display: grid;
    grid-template-rows: 10% auto;
    overflow-y: auto;
`;

const HamburgerMenu = styled.div<IDisplayMenu>`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 20%;
    flex-direction: column;
    height: 100%;
    border-right: ${props => (props.displayMenu ? "none" : " 1px solid #e5e5e5")};

    .faBars {
        display: ${props => (props.displayMenu ? "none" : "block")};
        margin-top: 12px;
        margin-bottom: auto;
    }

    @media (min-width: 768px) {
        display: none;
    }
`;

const agentHasId = (id?: number) => (agent: IAgent) => id === agent.id;

const AgentsTab = (props: IProps) => {
    const [selectedAgentId, setSelectedAgentId] = React.useState(undefined as unknown as number | undefined);
    const [errorMessage, setErrorMessage] = React.useState("");
    const [refresh, setRefresh] = React.useState(0);
    const [showAddAgentDialog, setShowAddAgentDialog] = React.useState(false);
    const [displayMenu, setDisplayMenu] = React.useState(false);

    const { webToken } = React.useContext(SessionContext).state;

    const agentListContainerReference = React.useRef(null as unknown as HTMLDivElement);

    const { data, loading, error } = useManagerAPI("agents", "GET", {}, webToken, refresh);
    const { data: disabledData, loading: disabledLoading, error: disabledError } = useManagerAPI("agents/disabled", "GET", {}, webToken, refresh);

    const agents = data ? [...data].sort((a, b) => a.name.localeCompare(b.name)) : [];
    const disabledAgents = disabledData ? [...disabledData].sort((a, b) => a.name.localeCompare(b.name)) : [];

    React.useEffect(() => {
        setRefresh(1);
    }, []);

    const changeSelectedAgent = (agent: IAgent) => () => {
        setSelectedAgentId(agent.id);
    };

    const refreshAgents = () => {
        setSelectedAgentId(undefined);
        setRefresh(refresh + 1);
    };

    const closeErrorDialog = () => {
        setErrorMessage("");
    };

    const buildAgentItem = (agent: IAgent, index: number) => {
        return <ListItem style={{ marginLeft: "8px" }} selected={selectedAgentId !== undefined && selectedAgentId === agent.id} key={`${agent.id}-${index}`} onClick={changeSelectedAgent(agent)}>
            <Label>
                <h2>{agent.name}</h2>
                <h3 style={{ textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden" }}>{agent.clientId}</h3>
            </Label>
        </ListItem>;
    };

    const selectedAgent = agents.concat(disabledAgents).find(agentHasId(selectedAgentId));

    const handleDisplayMenu = () => {
        setDisplayMenu(!displayMenu);
    };

    return <ViewContainer style={{ backgroundColor: "#fafafa" }}>
        <CreateAgentDialog
            show={showAddAgentDialog}
            onClose={() => setShowAddAgentDialog(false)}
            onConfirmAgentAdd={() => { setShowAddAgentDialog(false); refreshAgents(); }}
        />
        <ErrorDialog message={errorMessage} onClose={closeErrorDialog} />
        <ManagerTitleBar viewName="Agents" viewDescription="Here you can create, delete, enable and disable agents.">
            <Button onClick={() => setShowAddAgentDialog(true)}>Add agent</Button>
        </ManagerTitleBar>
        <ViewContainer style={{ display: "flex", flexDirection: "row" }}>
            <AgentListContainer displayMenu={displayMenu}>
                <LoadingIndicator type="Linear" show={loading || disabledLoading} />
                <AgentList ref={agentListContainerReference} style={{ flex: "1 1 auto" }}>
                    {data && agentListContainerReference.current ? <div>
                        <AgentHeader style={{ gridColumn: "1", width: "90%" }}>
                            <p>Enabled Agents</p>                            
                        </AgentHeader>
                        {agents.map(buildAgentItem)}
                        <AgentHeader>Disabled Agents</AgentHeader>
                        {disabledAgents.map(buildAgentItem)}
                    </div> : null}
                    {error && error}
                    {disabledError && disabledError}
                </AgentList>
            </AgentListContainer>
            <HamburgerMenu onClick={handleDisplayMenu} displayMenu={displayMenu}>
                <FontAwesomeIcon className='faBars' icon={faBars} style={{ transform: "scale(1)", zIndex: "1" }} />
            </HamburgerMenu>
            {!selectedAgent && <EmptyState />}
            {selectedAgent && <AgentDetails
                key={selectedAgentId}
                selectedAgent={selectedAgent}
                webToken={webToken}
                refreshAgents={refreshAgents}
                setErrorMessage={setErrorMessage}
            />}
        </ViewContainer>
    </ViewContainer>;
};

const EmptyState = () => <EmptyStateSplashContainer>
    <img alt="No agent selected" src={transparentRoleIcon} style={{ marginBottom: "8px", opacity: 0.3 }} />
    <h1>No agent selected</h1>
    <h2>Select an agent on the left to view its details.</h2>
</EmptyStateSplashContainer>;

const ErrorDialog = ({ message, onClose }: { message: string, onClose: () => void }) => <Dialog open={message !== ""} onClose={onClose}>
    <DialogTitle>Error</DialogTitle>
    <DialogContent style={{ lineHeight: "25px" }}>{message}</DialogContent>
    <DialogActions><Button onClick={onClose}>OK</Button></DialogActions>
</Dialog>;

export default AgentsTab;
